<script>
/**
 * @component VademecumList
 * @description ABM del vademécum de medicamentos: listado con filtro por categoría,
 *              búsqueda, paginación, alta, edición y borrado.
 * @example <VademecumList />
 */
import medicationCatalogService from "@/services/medication-catalog";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 500;

const CATEGORY_OPTIONS = [
  { value: "brand", label: "Marca" },
  { value: "generic", label: "Genérico" },
  { value: "phytotherapic", label: "Fitoterápico" },
  { value: "otc", label: "Venta libre" },
];

export default {
  name: "VademecumList",

  components: { Spinner },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      filterCategory: "",
      medications: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      isDialogVisible: false,
      dialogMode: "create",
      editingId: null,
      form: {
        tradeName: "",
        drug: "",
        presentation: "",
        laboratory: "",
        category: "",
        requiresPrescription: false,
        controlled: false,
      },
      formErrors: {},
      categoryOptions: CATEGORY_OPTIONS,
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),
  },

  created() {
    this.fetchMedications();
  },

  methods: {
    /**
     * @description Obtiene los medicamentos del backend con los filtros aplicados
     */
    async fetchMedications() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterCategory) params.category = this.filterCategory;

        const result = await medicationCatalogService.query(params);
        this.medications = result.docs;
        this.total = result.total;
      } catch (error) {
        this.$message.error("Error al cargar medicamentos");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    debounceSearch: debounce(function () {
      this.currentPage = 1;
      this.fetchMedications();
    }, DEBOUNCE_MS),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchMedications();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchMedications();
    },

    /**
     * @description Devuelve el label legible de una categoría
     * @param {string} category - Valor de categoría
     * @returns {string} Label
     */
    getCategoryLabel(category) {
      const opt = CATEGORY_OPTIONS.find((o) => o.value === category);
      return opt ? opt.label : category || "-";
    },

    /**
     * @description Abre el dialog de creación o edición
     * @param {string} mode - 'create' o 'edit'
     * @param {Object} [medication] - Medicamento a editar
     */
    openDialog(mode = "create", medication = null) {
      this.dialogMode = mode;
      this.isDialogVisible = true;
      this.formErrors = {};
      if (mode === "edit" && medication) {
        this.editingId = medication._id;
        this.form = {
          tradeName: medication.tradeName || "",
          drug: medication.drug || "",
          presentation: medication.presentation || "",
          laboratory: medication.laboratory || "",
          category: medication.category || "",
          requiresPrescription: medication.requiresPrescription || false,
          controlled: medication.controlled || false,
        };
      } else {
        this.resetForm();
      }
    },

    closeDialog() {
      this.isDialogVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.editingId = null;
      this.form = {
        tradeName: "",
        drug: "",
        presentation: "",
        laboratory: "",
        category: "",
        requiresPrescription: false,
        controlled: false,
      };
      this.formErrors = {};
    },

    /**
     * @description Valida y guarda el medicamento (crea o actualiza)
     */
    async saveMedication() {
      this.formErrors = {};
      if (!this.form.tradeName)
        this.formErrors.tradeName = "Nombre comercial requerido";
      if (!this.form.drug)
        this.formErrors.drug = "Droga / principio activo requerido";
      if (Object.keys(this.formErrors).length) return;

      try {
        if (this.dialogMode === "create") {
          await medicationCatalogService.create(this.form);
          this.$message.success("Medicamento creado");
        } else {
          await medicationCatalogService.update(this.editingId, this.form);
          this.$message.success("Medicamento actualizado");
        }
        this.closeDialog();
        this.fetchMedications();
      } catch (error) {
        this.$message.error("Error al guardar medicamento");
        console.error(error);
      }
    },

    /**
     * @description Elimina un medicamento previa confirmación
     * @param {Object} medication - Medicamento a eliminar
     */
    async deleteMedication(medication) {
      try {
        await this.$confirm(
          `¿Eliminar "${medication.tradeName}"?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await medicationCatalogService.remove(medication._id);
        this.$message.success("Medicamento eliminado");
        this.fetchMedications();
      } catch {
        // Cancelado
      }
    },
  },
};
</script>

<template lang="pug">
section
  header.headline
    .headline__actions
      el-select(
        v-model="filterCategory"
        placeholder="Categoría"
        clearable
        @change="onFilterChange"
        size="small"
      )
        el-option(
          v-for="opt in categoryOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        )
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        @input="debounceSearch"
        placeholder="Buscar por nombre o droga"
        :clearable="true"
        size="small"
      )
      .button.button--blue(@click="openDialog('create')" v-if="isAdmin")
        micon(name="add_circle_outline").button__icon
        span.button__text Nuevo Medicamento

  .box.box--with-subnav
    .box__content--stretch
      table
        thead
          tr
            th Nombre Comercial
            th Droga
            th Presentación
            th Laboratorio
            th Categoría
            th Acciones
        tbody
          spinner(v-if="isLoading")
          tr(
            v-else
            v-for="med in medications"
            :key="med._id"
            @click="openDialog('edit', med)"
          )
            td {{ med.tradeName }}
            td {{ med.drug }}
            td {{ med.presentation || '-' }}
            td {{ med.laboratory || '-' }}
            td {{ getCategoryLabel(med.category) }}
            td.actions
              .actions-container(@click.stop)
                button.button.button--action(@click="openDialog('edit', med)")
                  micon(name="edit")
                button.button.button--action(@click="deleteMedication(med)" v-if="isAdmin")
                  micon(name="delete_forever")

    el-pagination(
      v-if="total > pageSize"
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="currentPage"
      @current-change="onPageChange"
    )

  el-dialog(
    :title="dialogMode === 'create' ? 'Nuevo Medicamento' : 'Editar Medicamento'"
    :visible.sync="isDialogVisible"
    width="720px"
  )
    el-form(label-position="left" label-width="140px" size="small")
      el-row(:gutter="16")
        el-col(:span="12")
          el-form-item(label="Nombre Comercial" required :error="formErrors.tradeName")
            el-input(v-model="form.tradeName" maxlength="150" placeholder="Ej: Bayaspirina")
        el-col(:span="12")
          el-form-item(label="Droga / Principio Activo" required :error="formErrors.drug")
            el-input(v-model="form.drug" maxlength="150" placeholder="Ej: Ácido acetilsalicílico")
      el-row(:gutter="16")
        el-col(:span="12")
          el-form-item(label="Presentación")
            el-input(v-model="form.presentation" maxlength="150" placeholder="Ej: Comprimidos x 20")
        el-col(:span="12")
          el-form-item(label="Laboratorio")
            el-input(v-model="form.laboratory" maxlength="100" placeholder="Ej: Bayer S.A.")
      el-row(:gutter="16")
        el-col(:span="12")
          el-form-item(label="Categoría")
            el-select(v-model="form.category" placeholder="Seleccionar categoría" clearable style="width: 100%")
              el-option(
                v-for="opt in categoryOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              )
        el-col(:span="12")
          el-form-item(label="Opciones")
            el-checkbox(v-model="form.requiresPrescription") Requiere receta
            br
            el-checkbox(v-model="form.controlled") Controlado

    template(#footer)
      el-button(@click="closeDialog") Cancelar
      el-button(type="primary" @click="saveMedication") Guardar
</template>

<style scoped>
.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search__input {
  width: 250px;
}

.el-select {
  width: 160px;
}

tbody td:hover {
  cursor: pointer;
}
</style>
